import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { mapBuildDto, mapQueryResult } from '@/shared/data/dtoMappers';
import { getErrorMessage } from '@/shared/data/errors';
import { mergeProjectFilters } from '@/shared/data/queryFilters';
import {
  buildContainsFilter,
  buildEqFilter,
  mergeFilters,
  type GridFilters,
} from '@/ui/composites/grid/gridFilters';
import type {
  BuildDto,
  BuildStatus,
  BuildStatusSummaryDto,
  QueryFilter,
  QueryResult,
  QuerySort,
} from '@flowqa/shared';

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (tokenOverride?: string | null): string => {
  const token = tokenOverride ?? getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

const normalizeNullableString = (value?: string | null): string | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const queryBuilds = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: { page: number; pageSize: number; sorts: QuerySort[]; filters: QueryFilter[] },
  tokenOverride?: string | null,
): Promise<QueryResult<BuildDto>> => {
  const token = ensureToken(tokenOverride);
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId);
  try {
    const result = await apiClient.post<QueryResult<BuildDto>>(
      'builds/query',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts: input?.sorts ?? [{ field: 'createdAt', direction: 'desc' }],
        filters: combinedFilters,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
    return mapQueryResult(result, mapBuildDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryBuildSummary = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: { page: number; pageSize: number; sorts: QuerySort[]; filters: QueryFilter[] },
  tokenOverride?: string | null,
): Promise<BuildStatusSummaryDto> => {
  const token = ensureToken(tokenOverride);
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId);
  try {
    return await apiClient.post<BuildStatusSummaryDto>(
      'builds/summary',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 1,
        sorts: input?.sorts ?? [],
        filters: combinedFilters,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const getBuildById = async (
  id: string,
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<BuildDto> => {
  const token = ensureToken(tokenOverride);
  try {
    const result = await apiClient.get<BuildDto>(`builds/${id}`, {
      retries: 1,
      orgId: organisationId ?? undefined,
      token,
    });
    return mapBuildDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const getBuildByPipelineRunNumber = async (
  pipelineId: string | null,
  runNumber: number | null,
  projectId?: string | null,
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<BuildDto | null> => {
  if (!pipelineId || runNumber === null || !Number.isFinite(runNumber) || runNumber < 1) {
    return null;
  }
  const filters = mergeFilters(
    buildEqFilter('pipelineId', pipelineId),
    buildContainsFilter('buildKey', String(runNumber)),
  );
  const result = await queryBuilds(
    projectId,
    organisationId,
    {
      page: 1,
      pageSize: 50,
      sorts: [{ field: 'startedAt', direction: 'desc' }],
      filters,
    },
    tokenOverride,
  );
  return result.items.find((item) => resolveBuildRunNumber(item.buildKey) === runNumber) ?? null;
};

export const createBuild = async (
  input: {
    projectId: string;
    source?: string | null;
    buildKey?: string | null;
    pipelineUrl?: string | null;
    commitSha?: string | null;
    branch?: string | null;
    status?: BuildStatus;
    startedAt?: string | null;
    finishedAt?: string | null;
    tags?: string[];
  },
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<BuildDto> => {
  const token = ensureToken(tokenOverride);
  const body = {
    projectId: input.projectId.trim(),
    source: normalizeNullableString(input.source),
    buildKey: normalizeNullableString(input.buildKey),
    pipelineUrl: normalizeNullableString(input.pipelineUrl),
    commitSha: normalizeNullableString(input.commitSha),
    branch: normalizeNullableString(input.branch),
    status: input.status,
    startedAt: input.startedAt === undefined ? undefined : input.startedAt,
    finishedAt: input.finishedAt === undefined ? undefined : input.finishedAt,
    tags: input.tags,
  };
  try {
    const result = await apiClient.post<BuildDto>('builds', body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapBuildDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const updateBuild = async (
  input: {
    id: string;
    source?: string | null;
    buildKey?: string | null;
    pipelineUrl?: string | null;
    commitSha?: string | null;
    branch?: string | null;
    status?: BuildStatus;
    startedAt?: string | null;
    finishedAt?: string | null;
    tags?: string[];
  },
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<BuildDto> => {
  const token = ensureToken(tokenOverride);
  const body = {
    source: normalizeNullableString(input.source),
    buildKey: normalizeNullableString(input.buildKey),
    pipelineUrl: normalizeNullableString(input.pipelineUrl),
    commitSha: normalizeNullableString(input.commitSha),
    branch: normalizeNullableString(input.branch),
    status: input.status,
    startedAt: input.startedAt === undefined ? undefined : input.startedAt,
    finishedAt: input.finishedAt === undefined ? undefined : input.finishedAt,
    tags: input.tags,
  };
  try {
    const result = await apiClient.patch<BuildDto>(`builds/${input.id}`, body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapBuildDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const deleteBuild = async (
  input: { id: string },
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken(tokenOverride);
  try {
    await apiClient.post<undefined>(`builds/${input.id}/delete`, undefined, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};

export const restoreBuild = async (
  input: { id: string },
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken(tokenOverride);
  try {
    await apiClient.post<undefined>(`builds/${input.id}/restore`, undefined, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};
